import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar';
import Header from '../../components/Dashboard/Header';
import Footer from '../../components/Dashboard/Footer';
import {
  getArchConfig,
  listDefinitions, listVersions,
  getValidationResult,
  getValidationHistory,
} from '../../utils/canvasApi';
import ArchValidationReport from '../../components/Dashboard/Architecture/ArchValidationReport';
import ValidationHistory from '../../components/Dashboard/Architecture/ValidationHistory';
import { Bullseye, Stack, CloudArrowUp, ClockHistory } from 'react-bootstrap-icons';

const sortVersions = (vers) =>
  [...vers].sort((a, b) => b.versionNumber - a.versionNumber);

// Darkened shade of the brand teal (--color-primary rgb(117,196,192)); the raw
// brand colour is too light to read as text, so we darken it to stay on-brand
// while keeping ~4.7:1 contrast. Used to highlight the definition name and the
// call-to-action inside the validation empty states.
const BRAND_HL = '#287d77';
const hlName = { color: BRAND_HL, fontWeight: 700 };
const hlAction = { color: BRAND_HL, fontWeight: 600 };

// ─────────────────────────────────────────────────────────────────────────────

const ArchitectureLayer = () => {
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeSidebarItem, setActiveSidebarItem] = useState(3);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  const [projectId, setProjectId] = useState('');
  const [definitions, setDefinitions] = useState([]);
  const [versionsByDef, setVersionsByDef] = useState({});
  const [savedDefinitionId, setSavedDefinitionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validationByDef, setValidationByDef] = useState({});
  const [activeVersionByDef, setActiveVersionByDef] = useState({});
  const [validationHistory, setValidationHistory] = useState([]);
  const [historyError, setHistoryError] = useState(false);

  const projectName = localStorage.getItem('selectedProjectName');
  const isLargeScreen = windowWidth >= 992;

  useEffect(() => { document.title = 'DCode | Architecture compliance'; }, []);

  useEffect(() => {
    if (location.state?.projectId) {
      setProjectId(location.state.projectId);
    } else {
      const lsId = localStorage.getItem('selectedProjectId');
      if (lsId) setProjectId(lsId);
    }
  }, [location.state]);

  const loadData = useCallback(async (pid) => {
    if (!pid) return;
    setLoading(true);
    setSavedDefinitionId(null);
    setValidationByDef({});
    setValidationHistory([]);
    setHistoryError(false);
    try {
      const [configResult, defsResult] = await Promise.allSettled([
        getArchConfig(pid),
        listDefinitions(pid),
      ]);

      const activeDefId = configResult.status === 'fulfilled'
        ? configResult.value?.activeDefinitionId ?? null
        : null;
      const activeVerId = configResult.status === 'fulfilled'
        ? configResult.value?.activeVersionId ?? null
        : null;
      if (activeDefId) setSavedDefinitionId(activeDefId);

      const defs = defsResult.status === 'fulfilled' ? (defsResult.value ?? []) : [];
      setDefinitions(defs);

      if (!activeDefId) return;

      const versResult = await listVersions(pid, activeDefId).catch(() => []);
      const sorted = sortVersions(versResult ?? []);
      setVersionsByDef({ [activeDefId]: sorted });

      if (sorted.length === 0) return;

      // Fetch the result for the *active* version the user pinned
      // (config.activeVersionId), not just the highest-numbered one — otherwise a
      // newer draft on top of a published version hides the published version's
      // result. Fall back to the latest published version (floating active
      // config), then to the newest version.
      const resolvedVersion =
        (activeVerId && sorted.find(v => v.id === activeVerId)) ||
        sorted.find(v => v.status === 'published') ||
        sorted[0];
      setActiveVersionByDef({ [activeDefId]: resolvedVersion });

      const valResult = await getValidationResult(pid, activeDefId, resolvedVersion.versionNumber).catch(() => null);
      setValidationByDef({ [activeDefId]: valResult });
      try {
        const history = await getValidationHistory(pid, activeDefId, resolvedVersion.versionNumber);
        setValidationHistory(history);
      } catch (err) {
        setHistoryError(true);
      }
    } catch (err) {
      console.error('[ArchitectureLayer] loadData failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (projectId) loadData(projectId);
  }, [projectId, loadData]);

  useEffect(() => {
    if (location.state?.refreshDefinitions && projectId) loadData(projectId);
  }, [location.state, projectId, loadData]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth >= 992);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getSidebarWidth = () => (!sidebarOpen ? '0px' : sidebarCollapsed ? '80px' : '280px');
  const toggleSidebar = () => setSidebarOpen(v => !v);
  const toggleUserDropdown = () => setUserDropdownOpen(v => !v);
  const handleSidebarCollapseChange = (collapsed) => setSidebarCollapsed(collapsed);

  // ─────────────────────────────────────────────────────────────────────────

  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);

  const activeDef = savedDefinitionId ? definitions.find(d => d.id === savedDefinitionId) : null;
  const activeVersions = savedDefinitionId ? (versionsByDef[savedDefinitionId] ?? []) : [];
  const activeVersion = savedDefinitionId ? (activeVersionByDef[savedDefinitionId] ?? null) : null;
  const activeResult = savedDefinitionId ? (validationByDef[savedDefinitionId] ?? null) : null;

  // Explain *why* there is no result, based on how far the setup has progressed,
  // instead of always telling the user to "set an active definition".
  const hasPublishedVersion = activeVersions.some(v => v.status === 'published');
  // Structured empty state: icon + heading + explanation, keyed to how far
  // the setup has progressed, so the void reads as guidance rather than an error.
  const emptyState =
    !activeDef
      ? {
        Icon: Bullseye, accent: '#6366f1',
        title: 'No active definition',
        body: (
          <>No architecture definition is active for this project. <span style={hlAction}>Set one as active</span> to enable validation.</>
        ),
      }
      : activeVersions.length === 0
        ? {
          Icon: Stack, accent: '#0d9488',
          title: 'No versions yet',
          body: (
            <><span style={hlName}>{activeDef.name}</span> has no versions yet. <span style={hlAction}>Create and publish a version</span> to enable validation.</>
          ),
        }
        : !hasPublishedVersion
          ? {
            Icon: CloudArrowUp, accent: '#d97706',
            title: 'No published version',
            body: (
              <><span style={hlName}>{activeDef.name}</span> has no published version yet. <span style={hlAction}>Publish a version, then push a commit</span> to trigger validation.</>
            ),
          }
          : {
            Icon: ClockHistory, accent: '#0369a1',
            title: 'Waiting for first validation',
            body: (
              <>No validation has run yet for <span style={hlName}>{activeDef.name}</span>. <span style={hlAction}>Push a commit</span> to trigger validation.</>
            ),
          };
  const EmptyIcon = emptyState.Icon;

  const handleCopyCommit = () => {
    if (!activeResult?.commitId) return;
    navigator.clipboard.writeText(activeResult.commitId);
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1500);
  };

  const STATUS_STYLE = {
    published: { background: '#f0fdfa', color: '#0f766e', border: '1px solid #5eead4' },
    draft: { background: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d' },
    archived: { background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' },
  };

  return (
    <div className="d-flex flex-column min-vh-100">

      <div style={{
        marginLeft: isLargeScreen ? getSidebarWidth() : '0px',
        transition: 'margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative', zIndex: 1030,
      }}>
        <Header
          sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}
          isCollapsed={sidebarCollapsed}
          userDropdownOpen={userDropdownOpen} toggleUserDropdown={toggleUserDropdown}
          pageTitle="Architecture definition"
        />
      </div>

      <Sidebar
        sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}
        activeItem={activeSidebarItem} onItemClick={setActiveSidebarItem}
        onCollapseChange={handleSidebarCollapseChange} initialCollapsed={true}
      />

      {sidebarOpen && !isLargeScreen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1040 }}
          onClick={toggleSidebar}
        />
      )}

      <main style={{
        marginLeft: isLargeScreen ? getSidebarWidth() : '0px',
        paddingTop: '70px',
        transition: 'margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        minHeight: 'calc(100vh - 140px)',
      }}>
        <div className="container-fluid px-4">

          {/* ── Page header — matches Code Quality Report layout ── */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            gap: 20, marginBottom: 28, marginTop: 20, flexWrap: 'wrap',
          }}>
            {/* Left — title + meta row */}
            <div>
              {/* Feature name sits as an eyebrow above the definition name, which is the real subject of the page */}
              {activeDef && (
                <div style={{
                  fontSize: 12, fontWeight: 700, color: '#94a3b8',
                  textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4,
                }}>
                  Architecture definition
                </div>
              )}
              <h1 className="display-9 fw-bold mb-0 text-gradient" style={{ margin: 0 }}>
                {activeDef?.name ?? 'Architecture definition'}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0, marginTop: 8 }}>

                <span style={{ fontSize: 14, color: '#475569' }}>
                  <strong style={{ color: '#1e293b' }}>Project name:</strong> {projectName ?? '—'}
                </span>

                {activeResult?.commitId && (
                  <>
                    <span style={{ margin: '0 10px', color: '#cbd5e1' }}>·</span>
                    <span style={{ fontSize: 14, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <strong style={{ color: '#1e293b' }}>Commit:</strong>
                      <span style={{ fontFamily: 'monospace' }}>{activeResult.commitId.substring(0, 8)}...</span>
                      <button
                        onClick={handleCopyCommit}
                        title="Copy full commit ID"
                        style={{
                          background: 'none', border: '1px solid #e2e8f0', borderRadius: 6,
                          padding: '1px 7px', cursor: 'pointer', fontSize: 12,
                          color: copied ? '#0f766e' : '#64748b',
                          transition: 'all 0.15s',
                        }}
                      >
                        {copied ? '✓' : '⎘'}
                      </button>
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Right — version status + pass/fail + date */}
            {activeResult && (
              <div style={{ textAlign: 'right', marginTop: 8, flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', marginBottom: 6 }}>
                  {activeVersion && (
                    <span style={{
                      fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 12,
                      ...(STATUS_STYLE[activeVersion.status] ?? STATUS_STYLE.draft),
                    }}>
                      v{activeVersion.versionNumber} · {activeVersion.status.charAt(0).toUpperCase() + activeVersion.status.slice(1)}
                    </span>
                  )}
                  <span style={{
                    fontSize: 13, fontWeight: 700, padding: '3px 14px', borderRadius: 20,
                    background: activeResult.passed ? '#e9f7f6' : '#fdeceb',
                    color: activeResult.passed ? '#0f766e' : '#c0413e',
                    border: `1px solid ${activeResult.passed ? '#bfe6e3' : '#f3b5b3'}`,
                  }}>
                    {activeResult.passed ? '✓ Passed' : '✕ Failed'}
                  </span>
                </div>
                {activeResult.validatedAt && (
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    Validated:{' '}
                    {new Date(activeResult.validatedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                  </div>
                )}
                {activeResult.validatedById === 'ci' && (
                  <div style={{ fontSize: 11, color: '#0369a1', marginTop: 3, fontWeight: 600 }}>
                    Auto-run on push
                  </div>
                )}
              </div>
            )}
          </div>

          {!loading && historyError && <p role="alert">Validation history could not be loaded. The latest available report is shown below.</p>}
          {!loading && <ValidationHistory results={validationHistory} selectedId={activeResult?.id}
            onSelect={result => setValidationByDef({ [savedDefinitionId]: result })} />}
          {loading ? (
            <div className="text-muted" style={{ padding: '30px 0', textAlign: 'center', fontSize: 13 }}>
              Loading…
            </div>
          ) : activeResult ? (
            <ArchValidationReport result={activeResult} />
          ) : (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              textAlign: 'center', padding: '72px 24px', minHeight: 380,
            }}>
              <div style={{
                width: 76, height: 76, borderRadius: '50%',
                background: `${emptyState.accent}14`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 22,
              }}>
                <EmptyIcon size={32} color={emptyState.accent} />
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
                {emptyState.title}
              </div>
              <div style={{ fontSize: 13.5, color: '#64748b', lineHeight: 1.6, maxWidth: 440 }}>
                {emptyState.body}
              </div>
            </div>
          )}

        </div>
      </main>

      <Footer sidebarOpen={sidebarOpen} />
    </div>
  );
};

export default ArchitectureLayer;
